import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Gift, Ticket, Sparkles, Check } from "lucide-react";

export default function Rewards() {
  const { refresh } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [points, setPoints] = useState(0);
  const [redemptions, setRedemptions] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [r, red] = await Promise.all([api.get("/rewards"), api.get("/redemptions")]);
    setRewards(r.data.rewards);
    setPoints(r.data.points);
    setRedemptions(red.data.redemptions);
  };
  useEffect(() => { load(); }, []);

  const redeem = async (reward) => {
    setBusy(reward.id);
    try {
      const { data } = await api.post(`/rewards/${reward.id}/redeem`);
      setPoints(data.credit.points);
      await refresh();
      await load();
      toast.success(`Redeemed ${reward.name} · code ${data.redemption.code}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-xs font-medium tracking-overline text-primary uppercase mb-2">Rewards store</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Redeem your points</h1>
        </div>
        <div className="border border-border px-4 py-3">
          <p className="text-xs tracking-overline uppercase text-muted-foreground">Your points</p>
          <p className="font-mono text-xl font-bold text-primary" data-testid="rewards-points">{points.toLocaleString("en-ZA")}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rewards-grid">
        {rewards.map((r, i) => {
          const affordable = points >= r.points_cost;
          const Icon = r.category === "voucher" ? Ticket : Gift;
          return (
            <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border-2 border-border bg-card p-6 flex flex-col hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(211,23,43,0.08)] transition-all duration-200"
              data-testid={`reward-${r.id}`}>
              <div className={`w-11 h-11 flex items-center justify-center rounded-2xl mb-4 ${r.category === "voucher" ? "bg-sunny/30 text-[#141414]" : "bg-mint/20 text-mint"}`}>
                <Icon size={20} />
              </div>
              <h3 className="font-bold">{r.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 flex-1">{r.description}</p>
              <div className="flex items-center justify-between mt-5">
                <p className="font-mono text-lg font-bold flex items-center gap-1">
                  <Sparkles size={15} className="text-primary" /> {r.points_cost.toLocaleString("en-ZA")}
                </p>
                <button onClick={() => redeem(r)} disabled={!affordable || busy === r.id}
                  data-testid={`redeem-btn-${r.id}`}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-primary text-white hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-40 disabled:hover:translate-y-0">
                  {busy === r.id ? "…" : affordable ? "Redeem" : "Not enough"}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {redemptions.length > 0 && (
        <div className="mt-12">
          <h2 className="text-lg font-bold tracking-tight mb-4">Your redemptions</h2>
          <div className="border-2 border-border rounded-2xl bg-card divide-y divide-border overflow-hidden" data-testid="redemptions-list">
            {redemptions.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-4" data-testid={`redemption-${r.id}`}>
                <div className="flex items-center gap-3">
                  <Check className="text-green-400" size={18} />
                  <div>
                    <p className="text-sm font-medium">{r.reward_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">Code {r.code}</p>
                  </div>
                </div>
                <p className="font-mono text-sm text-muted-foreground">-{r.points_cost} pts</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
